import { History } from "history";
import NProgress from "nprogress"; // tslint:disable-line match-default-export-name
import React from "react";
import { IntlProvider } from "react-intl";
import { TransitionGroup } from "react-transition-group";

import { CanvasPageThrobber } from "@use/react-canvas-throbber";
import {
  Route,
  RouteErrorBoundary,
  RouteNotFound,
  Router,
} from "@use/react-router";

import { routes, urls } from "@apps/viewer/src/ts/config";

import { loadLocale } from "@apps/viewer/src/ts/i18n/loadLocale";
import { LocaleProvider } from "@apps/viewer/src/ts/i18n/LocaleContext";

import { createStore, Store, StoreProvider } from "@apps/viewer/src/ts/store";

import { BlockTransition } from "@apps/viewer/src/ts/components/block/transitions/BlockTransition";
import { InitialFade } from "@apps/viewer/src/ts/components/InitialFade";
import {
  CSS,
  Header,
  Progressbar,
  Wrapper,
} from "@apps/viewer/src/ts/components/layout";
import { ElementLightbox } from "@apps/viewer/src/ts/components/utils/ElementLightbox";

import { ProgressionPrompt } from "@apps/viewer/src/ts/components/layout/Overlays/ProgressionPrompt/ProgressionPrompt";
import { ProgressionPromptFade } from "@apps/viewer/src/ts/components/layout/Overlays/ProgressionPrompt/transitions/ProgressionPromptFade";
import { GrafanaFaroEventTrackingProvider } from "@provider/tracking";
import { AppContextProvider } from "./provider.app-context";
import { Tracker } from "./provider.tracker";

// Configure NProgress once and for all as soon as the application is being
// bootstrapped
NProgress.configure({
  // @ts-ignore There's a bug in the typings of NProgress, it's not defining
  // the trickleRate, even though it's a valid configuration!
  trickleRate: 0.06,
  trickleSpeed: 150,
});

/**
 * Static error object that we're going to throw inside renderBlock, if we're
 * not able to identify the currently selected block (we use a static error so
 * we can safely perform === check in catch route and fall back to the not found
 * message).
 */
const blockNotFoundError = new Error("Unable to find block with given id.");

/**
 * Specification of props needed to render <Root />.
 */
type RootProps = {
  /**
   * The id of the course that should be modified within this instance of the
   * builder.
   */
  courseId: string;
};

/**
 * Specification of the runtime state of <Root />.
 */
type RootState = {
  /**
   * Specifies if a route has been opened by the router so far (we're going to
   * rely on this piece of information to determine when to hide the throbber
   * that's displayed initially).
   */
  wasRouteOpened?: boolean;
};

/**
 * Root of the application render tree.
 *
 * We're going to mount the basic layout of the application, a router to manage
 * page rendering and an initial throbber which is going to be displayed until
 * the application is ready to be rendered.
 */
class RootUnwrapped extends React.PureComponent<RootProps, RootState> {
  /**
   * Set up initial state, so we can safely use this.state prior to the first
   * setState call!
   */
  public readonly state: RootState = {};

  /**
   * Specifies if we've rendered the first block yet. This is used to identify
   * when the initial render has completed.
   */
  protected hasRenderedBlock: boolean = false;

  /**
   * Specifies whether or not the initial render has completed.
   */
  protected initialRender: boolean = true;

  /**
   * Contains a reference to the store that's backing the lifecycle of the
   * application.
   */
  protected store: Store | undefined;

  /**
   * Mount the application render tree here.
   */
  public render(): React.JSX.Element {
    return (
      <AppContextProvider store={this.store}>
        <GrafanaFaroEventTrackingProvider>
          <Router
            basePath={urls.basePath.replace(":courseId", this.props.courseId)}
            onRouteRequested={this.onRouteRequested}
            onRouteOpened={this.onRouteOpened}
          >
            <Route path="/" render={this.renderBlock} />
            <Route path={routes.block} render={this.renderBlock} />
            <RouteNotFound render={this.renderNotFound} />
            <RouteErrorBoundary render={this.renderErrorBoundary} />

            {this.renderThrobber()}
            {this.renderElementLightbox()}
          </Router>

          <Tracker />
        </GrafanaFaroEventTrackingProvider>
      </AppContextProvider>
    );
  }

  /**
   * Helper that'll render a throbber that's going to be displayed while we're
   * waiting for data to be ready.
   */
  protected renderThrobber(): React.JSX.Element | null {
    return (
      <InitialFade in={!this.state.wasRouteOpened}>
        <CanvasPageThrobber />
      </InitialFade>
    );
  }

  /**
   * Rendereder that'll render the progression prompt when the user revists
   * the page.
   */
  protected renderProgressionPrompt(): React.ReactNode {
    if (!this.store) {
      return;
    }

    const lastProgression = this.store.progression.lastLocation;

    if (!lastProgression) {
      return;
    }

    const displayProgressionPrompt =
      this.store && this.store.displayProgressionPrompt;

    return (
      <ProgressionPromptFade in={displayProgressionPrompt}>
        {(ref) => (
          <div ref={ref}>
            <ProgressionPrompt
              lastProgressionSrc={lastProgression.src}
              closePrompt={this.closeProgressionPrompt}
            />
          </div>
        )}
      </ProgressionPromptFade>
    );
  }

  /**
   * Renderer that'll inject the ElementLightbox utility, which will make
   * sure that any currently selected lightbox will be displayed.
   */
  protected renderElementLightbox(): React.JSX.Element | undefined {
    if (!this.store) {
      return undefined;
    }

    return <ElementLightbox store={this.store} />;
  }

  /**
   * Helper that'll render the block with the id specified in the current
   * path of the browser.
   */
  protected renderBlock = async (
    path: string,
    params: { blockId?: string },
  ) => {
    // When we get here, then
    this.hasRenderedBlock = true;

    const { renderBlock } = await import(
      "@apps/viewer/src/ts/components/block/renderBlock"
    );

    return this.wrapRender(async (store) => {
      // Determine id of the block that should be displayed
      const blockId =
        params.blockId !== undefined
          ? params.blockId.replace(/^\//, "")
          : (store.course.getIdByIndex(0) as string | undefined);

      const block =
        blockId !== undefined ? store.course.getById(blockId) : undefined;

      // Bail out if the block doesn't exist within the course
      if (block === undefined) {
        throw blockNotFoundError;
      }

      // Get index of previously and currently rendered block, so we can
      // determine if the transition should be reversed
      const prevIndex = store.course.getIndexById(store.blockId);
      const nextIndex = store.course.getIndexById(block.id);

      const reversedTransition =
        prevIndex !== -1 && nextIndex !== -1 && nextIndex < prevIndex;

      // Register currently selected block
      store.blockId = block.id;

      // ... Otherwise render the desired block
      return (
        <TransitionGroup key="block" component={undefined}>
          <BlockTransition key={block.id} reversed={reversedTransition}>
            {await renderBlock({ model: block, store })}
          </BlockTransition>
        </TransitionGroup>
      );
    });
  };

  /**
   * Helper that'll render a fallback error message if the currently requested
   * url doesn't exist within the application.
   */
  protected renderNotFound = async (path: string) => {
    const { PageNotFound } = await import(
      "@apps/viewer/src/ts/components/errors/PageNotFound/PageNotFound"
    );

    // Register that initial render is no longer the case
    this.initialRender = false;

    return this.wrapRender(() => <PageNotFound key="notFound" />);
  };

  /**
   * Helper that'll render a fallback error message, if an exception was
   * thrown during rendering of one of the pages within the router.
   */
  protected renderErrorBoundary = async (path: string, err: unknown) => {
    // If the blockNotFoundError was thrown, then render the not found
    // message rather than the generic error boundary
    if (err === blockNotFoundError) {
      return this.renderNotFound(path);
    }

    // log error for easier debugging
    // tslint:disable-next-line no-console
    console.error(err);

    // ... Otherwise render the generic client error message
    const { ClientError } = await import(
      "@apps/viewer/src/ts/components/errors/ClientError/ClientError"
    );

    // Register that initial render is no longer the case
    this.initialRender = false;

    return this.wrapRender(() => <ClientError key="clientError" />);
  };

  /**
   * Listener that's triggered when the user requests opening of a route (ie.
   * right after he clicks a link, but before the content is actually ready
   * for rendering).
   */
  protected onRouteRequested = () => {
    // Start running progressbar while we're waiting for content to be
    // fetched...
    if (this.state.wasRouteOpened) {
      NProgress.start();
    }
  };

  /**
   * Listener that's going to be triggered whenever a route has been opened
   * (ie. requested and rendered).
   *
   * Here we're going to register that a route has been successfully opened,
   * so that we can hide the throbber.
   */
  protected onRouteOpened = (path: string, history: History) => {
    if (!this.state.wasRouteOpened) {
      this.setState({ wasRouteOpened: true });

      // let parent windows know that a route has been opened and that
      // any throbbers can now fade out
      window.parent.postMessage("course-viewer:ready", "*");
    }

    // Stop the progress bar, now that content is ready for rendering...
    NProgress.done();

    // Ensure we scroll to the top after navigating to a new page since we're
    // the user is starting to interact with new content and hence should be
    // presented with the start of it, even if they had scrolled on the prev page.
    window.scrollTo(0, 0);

    // Bail out if store is unavailable, as we won't be able to perform any
    // of the following actions in that case
    if (!this.store) {
      return;
    }

    // If this was the initial load of the page, and the user hasn't loaded
    // a specific block, then prompt him to ask if he'd like to continue
    // from last
    const lastLocation = this.store.progression.lastLocation;

    if (
      lastLocation &&
      path === "/" &&
      lastLocation.src !== "/" &&
      this.initialRender
    ) {
      this.store.displayProgressionPrompt = true;
    }

    // Extract reference to the currently selected block
    const blockIndex = this.store.course.getIndexById(this.store.blockId);
    const block = this.store.course.getByIndex(blockIndex);

    // Bail out if block couldn't be identified
    if (!block) {
      return;
    }

    // Actually update the progression based on the currently selected block
    if (!this.initialRender && !this.store.displayProgressionPrompt) {
      this.store.progression
        .update({
          src: path,
          title: block.title,
          completion: Math.round(
            (blockIndex / (this.store.course.size - 1)) * 100,
          ),
        })
        .catch((err) => {
          throw err;
        });
    }

    // If we've successfully renderd a block, then indicate that the initial
    // render has completed!
    if (this.hasRenderedBlock) {
      this.initialRender = false;
    }

    // Finally, begin preloading the next and previous block in order for
    // faster page changes in the future!
    this.preloadBlock(blockIndex - 1);
    this.preloadBlock(blockIndex + 1);
  };

  /**
   * Helper that'll wrap the given render method in a promise, so that we can
   * defer rendering until required data is available.
   */
  protected async wrapRender(
    render: (store: Store) => Promise<React.JSX.Element> | React.JSX.Element,
  ): Promise<React.JSX.Element> {
    // At some point we might want to apply locale as a state variable that
    // could easily be changed, but for now it's simply hardcoded here
    const locale = "da-dk";

    // Wait for all required bundles and data to become available before
    // moving on to render the actual page
    const [store, localeData] = await Promise.all([
      createStore(this.props.courseId),
      loadLocale(locale),
    ]);

    // Cache store object for future use
    this.store = store;

    // Actually render the content
    return (
      <StoreProvider value={store} key="root">
        <LocaleProvider value={localeData}>
          <IntlProvider
            locale={locale}
            messages={localeData}
            textComponent="span"
          >
            <>
              <CSS />
              <Header visible={this.state.wasRouteOpened} />

              <InitialFade in={this.state.wasRouteOpened}>
                <Wrapper>{await render(store)}</Wrapper>
              </InitialFade>

              {this.renderProgressionPrompt()}

              <Progressbar visible={this.state.wasRouteOpened} />
            </>
          </IntlProvider>
        </LocaleProvider>
      </StoreProvider>
    );
  }

  /**
   * Will be triggered once a button in the ProgressionPrompt is pressed, in
   * order to close it
   */
  protected closeProgressionPrompt = () => {
    if (this.store) {
      this.store.displayProgressionPrompt = false;

      // Since the store is created in this component, then we cannot at
      // the @observer decorator.. Instead we manually force an update after
      // updating the store
      this.forceUpdate();
    }
  };

  private readonly preloadBlock = (blockIndex: number) => {
    const store = this.store;

    if (!store) {
      return;
    }

    const model =
      blockIndex >= 0 && blockIndex < store.course.size
        ? store.course.getByIndex(blockIndex - 1)
        : undefined;

    if (!model) {
      return;
    }

    import("@apps/viewer/src/ts/components/block/renderBlock")
      .then(({ renderBlock }) => renderBlock({ model, store }))
      .catch(() => {
        /* no-op */
      });
  };
}

// tslint:disable-next-line variable-name
export const Root = process.env.WATCH_MODE ? RootUnwrapped : RootUnwrapped;
